import React, { useMemo } from 'react'
import { Grid, Box, Paper, Typography } from '@mui/material'
import { Bar, Line } from 'react-chartjs-2'
import { operatorReportStyles as opStyles } from '../../../helpers/style'
import { CALL_DIRECTIONS, CALL_TYPES, normalizeDisposition, normalizeDirection } from '../../../helpers/demo'
import { directionCountsFromStatusFacet, ivrMusicVoiceCountsFromFacets, statusLabelToDirection } from './reportFacets'

function mapDocToCall(doc) {
  // Support both flat doc (start/disposition/status at top level) and nested (doc.items)
  const main = doc?.items?.length
    ? (doc.items.find((i) => i.uniqueid === doc.linkedid) || doc.items[0])
    : (doc?.start != null || doc?.disposition != null ? doc : null)
  if (!main) return null
  const direction = main.status || main.direction || (main.line && main.line !== 'null' ? 'outgoing' : 'incoming')
  return {
    start: main.start,
    disposition: main.disposition,
    direction,
    status: main.status,
  }
}

const STACKED_AREA_COLORS = [
  '#c8f4dc',
  '#d6ebf7',
  '#e8ecec',
  '#fcecc9',
  '#fbd9d4',
  '#dfe2e4',
]
const STACKED_AREA_BORDER_COLORS = [
  '#2ECC71', '#3498DB', '#95A5A6', '#F39C12', '#E74C3C', '#6C757D',
]

function getPeriodKey(dateStr, period) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (period === 'today') return String(d.getUTCHours()).padStart(2, '0')
  if (period === 'month') return String(d.getUTCDate())
  // Prefer UTC date parts so ISO Solr timestamps align with selected range
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatPeriodLabel(key, period, langIndex, dateFrom, dateTo) {
  if (!key) return key
  const locale = langIndex === 2 ? 'ru-RU' : langIndex === 3 ? 'hy-AM' : 'en-US'
  if (period === 'today') return `${key}:00`
  if (period === 'month') return key
  if (period === 'week') {
    const [y, mo, d] = key.split('-')
    const date = new Date(parseInt(y, 10), parseInt(mo, 10) - 1, parseInt(d, 10))
    return date.toLocaleDateString(locale, { weekday: 'short' })
  }
  const [y, m, day] = key.split('-')
  const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${parseInt(day, 10)} ${monthNames[parseInt(m, 10)] || m}`
}

function parseYmd(dateStr) {
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map((n) => parseInt(n, 10))
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

function getFullPeriodKeys(period, dateFrom, dateTo) {
  if (period === 'today') {
    return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
  }
  // week / custom / any explicit from–to range: one key per calendar day
  if (period !== 'month' && dateFrom && dateTo) {
    const from = parseYmd(dateFrom)
    const to = parseYmd(dateTo)
    if (!from || !to) return []
    const keys = []
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      keys.push(`${y}-${m}-${day}`)
    }
    return keys
  }
  if (period === 'month' && (dateFrom || dateTo)) {
    const ref = dateTo ? parseYmd(dateTo) : dateFrom ? parseYmd(dateFrom) : new Date()
    if (!ref) return []
    const year = ref.getFullYear()
    const month = ref.getMonth()
    const lastDay = new Date(year, month + 1, 0).getDate()
    return Array.from({ length: lastDay }, (_, i) => String(i + 1))
  }
  return []
}

function bucketDateKey(val) {
  if (val == null) return ''
  const raw = String(val)
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`
  const d = new Date(val)
  if (!Number.isNaN(d.getTime())) {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
  }
  return ''
}

function emptyOutcomeRow() {
  return { answered: 0, answered_later: 0, not_answered: 0, busy: 0, failed: 0, canceled: 0 }
}

const OUTCOME_KEYS = ['answered', 'answered_later', 'not_answered', 'busy', 'failed', 'canceled']

function dispValToKey(val) {
  const v = String(val || '').toUpperCase().trim()
  if (v === 'ANSWERED') return 'answered'
  if (v === 'ANSWER LATER') return 'answered_later'
  if (v === 'NO ANSWER' || v === 'NOANSWER') return 'not_answered'
  if (v === 'BUSY') return 'busy'
  if (v === 'FAILED') return 'failed'
  if (v === 'CANCEL' || v === 'CANCELED') return 'canceled'
  return null
}

// Map over graphic buckets and sum by_disp counts (optionally filtered by direction when by_type exists)
function outcomeCountsFromGraphicBuckets(buckets, directionFilter) {
  const totals = Object.fromEntries(OUTCOME_KEYS.map((k) => [k, 0]))
  if (!buckets || !Array.isArray(buckets)) return totals
  const allDispBuckets = buckets.flatMap((b) => {
    const byType = b?.by_type?.buckets
    if (Array.isArray(byType)) {
      if (!directionFilter) return byType.flatMap((t) => t?.by_disp?.buckets || [])
      return byType
        .filter((t) => statusLabelToDirection(t?.val) === directionFilter)
        .flatMap((t) => t?.by_disp?.buckets || [])
    }
    // Only use top-level by_disp when by_type is absent
    const byDisp = b?.by_disp?.buckets
    if (Array.isArray(byDisp)) return byDisp
    return []
  })
  allDispBuckets.forEach((d) => {
    const key = dispValToKey(d?.val)
    if (key && totals[key] !== undefined) totals[key] += Number(d.count) || 0
  })
  return totals
}

function outcomeTotalsAreEmpty(totals) {
  if (!totals) return true
  return OUTCOME_KEYS.every((k) => !(Number(totals[k]) > 0))
}

export default function DirectionsPage({
  report,
  period = 'week',
  dateFrom,
  dateTo,
  pieChartData,
  directionChartData,
  opTr,
  langIndex,
  getCallTypeLabel,
  getDirectionLabel,
  statusDirection = 'incoming',
  historyData,
  historyFacetFields,
  graphicStatusData,
  graphicDispositionData,
  historyGraphicLoading,
  historyGraphicError,
}) {
  const facetFields = historyFacetFields ?? historyData?.facet_counts?.facet_fields ?? null
  const callsForCharts = useMemo(() => {
    if (historyData != null) {
      const docs = historyData?.response?.docs
      const list = Array.isArray(docs) ? docs : []
      return list.map(mapDocToCall).filter(Boolean)
    }
    return report?.calls ?? []
  }, [historyData, historyData?.response?.docs, report?.calls])

  const stackedAreaData = useMemo(() => {
    const graphic = graphicStatusData || graphicDispositionData
    const buckets = graphic?.buckets
    if (buckets && Array.isArray(buckets) && buckets.length > 0) {
      const keys = OUTCOME_KEYS
      const byBucketByType = buckets.map((b) => {
        const row = emptyOutcomeRow()
        const addDispBuckets = (dispBuckets) => {
          if (!Array.isArray(dispBuckets)) return
          dispBuckets.forEach((d) => {
            const key = dispValToKey(d?.val)
            if (key && row[key] !== undefined) row[key] += Number(d.count) || 0
          })
        }
        const byType = b?.by_type?.buckets
        if (Array.isArray(byType)) {
          byType.forEach((t) => {
            const dir = statusLabelToDirection(t?.val)
            if (!statusDirection || dir === statusDirection) {
              addDispBuckets(t?.by_disp?.buckets)
            }
          })
        } else {
          const byDisp = b?.by_disp?.buckets
          if (Array.isArray(byDisp)) {
            addDispBuckets(byDisp)
          }
        }
        return row
      })
      const graphicHasDirectionData = byBucketByType.some((row) =>
        OUTCOME_KEYS.some((k) => Number(row[k]) > 0)
      )
      if (graphicHasDirectionData || !statusDirection) {
        // Pad missing days for custom/week ranges so Aug 1–7 shows all 7 days
        const fullKeys = period !== 'today' ? getFullPeriodKeys(period, dateFrom, dateTo) : []
        let labels
        let rows
        if (fullKeys.length > 0) {
          const byDate = {}
          buckets.forEach((b, i) => {
            const dateKey = bucketDateKey(b?.val)
            if (!dateKey) return
            if (!byDate[dateKey]) byDate[dateKey] = emptyOutcomeRow()
            OUTCOME_KEYS.forEach((k) => {
              byDate[dateKey][k] += Number(byBucketByType[i]?.[k]) || 0
            })
          })
          rows = fullKeys.map((k) => byDate[k] || emptyOutcomeRow())
          labels = fullKeys.map((k) => formatPeriodLabel(k, period, langIndex, dateFrom, dateTo))
        } else {
          const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          labels = buckets.map((b) => {
            if (!b || b.val == null) return '—'
            const raw = String(b.val)
            const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
            if (isoMatch) {
              const [, y, m, day, hh, mm] = isoMatch
              const month = monthNames[parseInt(m, 10)] || m
              return `${parseInt(day, 10)} ${month}, ${hh}:${mm}`
            }
            const d = new Date(b.val)
            if (!Number.isNaN(d.getTime())) {
              const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
              return `${datePart}, ${timePart}`
            }
            return raw
          })
          rows = byBucketByType
        }
        const datasets = keys.map((k, i) => {
          const def = CALL_TYPES.find((t) => t.key === k)
          const color = STACKED_AREA_COLORS[i] ?? '#94a3b8'
          const borderColor = STACKED_AREA_BORDER_COLORS[i] ?? '#475569'
          return {
            label: getCallTypeLabel ? getCallTypeLabel(k) : (opTr[def?.labelKey]?.[langIndex] ?? k),
            data: rows.map((row) => row[k] ?? 0),
            borderColor,
            backgroundColor: color,
            pointBackgroundColor: borderColor,
            pointBorderColor: borderColor,
            pointBorderWidth: 1,
            fill: true,
            stack: 'stack0',
            tension: 0.2,
          }
        })
        return { labels, datasets }
      }
      // Fall through to calls-based chart when graphic has no rows for this direction
    }
    const keys = ['answered', 'answered_later', 'not_answered', 'busy', 'failed', 'canceled']
    const reportKeys = { not_answered: 'notAnswered', answered_later: 'answeredLater' }
    const byPeriod = {}
    const callsToUse = callsForCharts.filter((c) => normalizeDirection(c) === statusDirection)
    callsToUse.forEach((c) => {
      const key = getPeriodKey(c.start, period)
      if (!key) return
      if (!byPeriod[key]) byPeriod[key] = { answered: 0, answeredLater: 0, notAnswered: 0, busy: 0, failed: 0, canceled: 0 }
      const type = normalizeDisposition(c.disposition)
      const reportKey = reportKeys[type] ?? type
      if (byPeriod[key][reportKey] !== undefined) byPeriod[key][reportKey] += 1
    })
    const fullKeys = getFullPeriodKeys(period, dateFrom, dateTo)
    const keysToUse = fullKeys.length > 0 ? fullKeys : Object.keys(byPeriod).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    keysToUse.forEach((k) => {
      if (!byPeriod[k]) byPeriod[k] = { answered: 0, answeredLater: 0, notAnswered: 0, busy: 0, failed: 0, canceled: 0 }
    })
    const sorted = keysToUse.map((k) => [k, byPeriod[k]])
    const labels = sorted.map(([k]) => formatPeriodLabel(k, period, langIndex, dateFrom, dateTo))
    const datasets = keys.map((k, i) => {
      const reportKey = reportKeys[k] ?? k
      const def = CALL_TYPES.find((t) => t.key === k)
      const color = STACKED_AREA_COLORS[i] ?? '#94a3b8'
      const borderColor = STACKED_AREA_BORDER_COLORS[i] ?? '#475569'
      return {
        label: getCallTypeLabel ? getCallTypeLabel(k) : (opTr[def?.labelKey]?.[langIndex] ?? k),
        data: sorted.map(([, v]) => v[reportKey] ?? 0),
        borderColor,
        backgroundColor: color,
        pointBackgroundColor: borderColor,
        pointBorderColor: borderColor,
        pointBorderWidth: 1,
        fill: true,
        stack: 'stack0',
        tension: 0,
      }
    })
    return { labels, datasets }
  }, [graphicStatusData, graphicDispositionData, callsForCharts, period, dateFrom, dateTo, statusDirection, langIndex, getCallTypeLabel, opTr])

  const outcomeKeys = OUTCOME_KEYS
  const ivrKeys = ['ivr', 'music_hold', 'voicemail']

  const graphicBuckets = (graphicStatusData || graphicDispositionData)?.buckets

  const directionBarData = useMemo(() => {
    const directionCounts = directionCountsFromStatusFacet(facetFields)
    const dirKeys = ['incoming', 'outgoing', 'local', 'transferred']
    const counts = dirKeys.map((d) =>
      directionCounts
        ? (Number(directionCounts[d]) || 0)
        : callsForCharts.reduce((sum, c) => sum + (normalizeDirection(c) === d ? 1 : 0), 0)
    )
    const total = counts.reduce((sum, n) => sum + n, 0)
    const labels = dirKeys.map((d) => getDirectionLabel ? getDirectionLabel(d) : (opTr[`dir${d.charAt(0).toUpperCase() + d.slice(1)}`]?.[langIndex] ?? d))
    const colors = dirKeys.map((d) => CALL_DIRECTIONS.find((x) => x.key === d)?.color ?? '#6b7280')
    return {
      labels,
      total,
      datasets: [{
        label: opTr.totalCallsCount?.[langIndex] ?? 'Total calls',
        data: counts,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      }],
    }
  }, [facetFields, callsForCharts, langIndex, getDirectionLabel, opTr])

  const selectedDirectionOutcomeBarData = useMemo(() => {
    const dir = statusDirection || 'incoming'
    const totals = Array.isArray(graphicBuckets) ? outcomeCountsFromGraphicBuckets(graphicBuckets, dir) : null
    const useGraphic = totals && !outcomeTotalsAreEmpty(totals)
    const data = useGraphic
      ? OUTCOME_KEYS.map((k) => totals[k] ?? 0)
      : outcomeKeys.map((k) =>
          callsForCharts.reduce(
            (sum, c) => sum + (normalizeDirection(c) === dir && normalizeDisposition(c.disposition) === k ? 1 : 0),
            0
          )
        )
    const labels = outcomeKeys.map((k) => (getCallTypeLabel ? getCallTypeLabel(k) : (opTr[CALL_TYPES.find((t) => t.key === k)?.labelKey]?.[langIndex] ?? k)))
    const colors = outcomeKeys.map((k) => CALL_TYPES.find((t) => t.key === k)?.color ?? '#6b7280')
    const dirLabel = getDirectionLabel ? getDirectionLabel(dir) : (opTr[`dir${dir.charAt(0).toUpperCase() + dir.slice(1)}`]?.[langIndex] ?? dir)
    return {
      labels,
      datasets: [{
        label: dirLabel,
        data,
        backgroundColor: colors,
        borderColor: colors.map((c) => c),
        borderWidth: 1,
      }],
    }
  }, [graphicBuckets, callsForCharts, statusDirection, langIndex, getCallTypeLabel, getDirectionLabel, opTr])

  const incomingOutcomeBarData = useMemo(() => {
    const totals = Array.isArray(graphicBuckets) ? outcomeCountsFromGraphicBuckets(graphicBuckets, 'incoming') : null
    const useGraphic = totals && !outcomeTotalsAreEmpty(totals)
    const data = useGraphic
      ? OUTCOME_KEYS.map((k) => totals[k] ?? 0)
      : outcomeKeys.map((k) =>
          callsForCharts.reduce(
            (sum, c) => sum + (normalizeDirection(c) === 'incoming' && normalizeDisposition(c.disposition) === k ? 1 : 0),
            0
          )
        )
    const labels = outcomeKeys.map((k) => (getCallTypeLabel ? getCallTypeLabel(k) : (opTr[CALL_TYPES.find((t) => t.key === k)?.labelKey]?.[langIndex] ?? k)))
    const colors = outcomeKeys.map((k) => CALL_TYPES.find((t) => t.key === k)?.color ?? '#6b7280')
    return {
      labels,
      datasets: [{
        label: opTr.totalIncoming?.[langIndex] ?? 'Total incoming',
        data,
        backgroundColor: colors,
        borderColor: colors.map((c) => c),
        borderWidth: 1,
      }],
    }
  }, [graphicBuckets, callsForCharts, langIndex, getCallTypeLabel, opTr])

  const outgoingOutcomeBarData = useMemo(() => {
    const totals = Array.isArray(graphicBuckets) ? outcomeCountsFromGraphicBuckets(graphicBuckets, 'outgoing') : null
    const useGraphic = totals && !outcomeTotalsAreEmpty(totals)
    const data = useGraphic
      ? OUTCOME_KEYS.map((k) => totals[k] ?? 0)
      : outcomeKeys.map((k) =>
          callsForCharts.reduce(
            (sum, c) => sum + (normalizeDirection(c) === 'outgoing' && normalizeDisposition(c.disposition) === k ? 1 : 0),
            0
          )
        )
    const labels = outcomeKeys.map((k) => (getCallTypeLabel ? getCallTypeLabel(k) : (opTr[CALL_TYPES.find((t) => t.key === k)?.labelKey]?.[langIndex] ?? k)))
    const colors = outcomeKeys.map((k) => CALL_TYPES.find((t) => t.key === k)?.color ?? '#6b7280')
    return {
      labels,
      datasets: [{
        label: opTr.totalOutgoing?.[langIndex] ?? 'Total outgoing',
        data,
        backgroundColor: colors,
        borderColor: colors.map((c) => c),
        borderWidth: 1,
      }],
    }
  }, [graphicBuckets, callsForCharts, langIndex, getCallTypeLabel, opTr])

  const ivrVoicemailBarData = useMemo(() => {
    const facetCounts = ivrMusicVoiceCountsFromFacets(facetFields)
    let data
    if (facetCounts) {
      data = [facetCounts.ivr, facetCounts.music_hold, facetCounts.voicemail]
    } else {
      const byType = {}
      ivrKeys.forEach((k) => { byType[k] = 0 })
      callsForCharts.forEach((c) => {
        const type = normalizeDisposition(c.disposition)
        if (byType[type] !== undefined) byType[type] += 1
      })
      data = ivrKeys.map((k) => byType[k])
    }
    const labels = ivrKeys.map((k) => (getCallTypeLabel ? getCallTypeLabel(k) : (opTr[CALL_TYPES.find((t) => t.key === k)?.labelKey]?.[langIndex] ?? k)))
    const colors = ivrKeys.map((k) => CALL_TYPES.find((t) => t.key === k)?.color ?? '#6b7280')
    return {
      labels,
      datasets: [{
        label: opTr.totalCallsCount?.[langIndex] ?? 'Total',
        data,
        backgroundColor: colors,
        borderColor: colors.map((c) => c),
        borderWidth: 1,
      }],
    }
  }, [facetFields, callsForCharts, langIndex, getCallTypeLabel, opTr])

  return (
    <Box sx={{ ...opStyles.sectionBox, mb: 4 }}>
      <Paper sx={{ ...opStyles.chartPaperWide, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ ...opStyles.chartTitle, mb: 1 }}>
          {opTr.callsByStatus?.[langIndex]} — {opTr.callsPerDay?.[langIndex]}
        </Typography>
        <Box sx={opStyles.chartBox}>
          <Line
            data={stackedAreaData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { position: 'top' },
                tooltip: { mode: 'index', intersect: false },
              },
              scales: {
                x: {
                  type: 'category',
                  stacked: true,
                  grid: { display: false },
                },
                y: {
                  stacked: true,
                  beginAtZero: true,
                  ticks: { stepSize: 1 },
                },
              },
            }}
          />
        </Box>
      </Paper>
      <Grid container spacing={3} sx={opStyles.gridMb}>
        <Grid item xs={12} md={4}>
          <Paper sx={opStyles.chartPaper}>
            <Typography variant="subtitle1" sx={opStyles.chartTitle}>
              {opTr.callsByDirection?.[langIndex]}
            </Typography>
            <Box sx={{ ...opStyles.chartBox, minHeight: 160 }}>
              <Bar
                data={directionBarData}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { beginAtZero: true, ticks: { stepSize: 1 } },
                    y: { grid: { display: false } },
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={opStyles.chartPaper}>
            <Typography variant="subtitle1" sx={opStyles.chartTitle}>
              {(() => {
                const d = statusDirection || 'incoming'
                const key = `${d}CallsByOutcome`
                if (opTr[key]?.[langIndex]) return opTr[key][langIndex]
                const dirLabel = getDirectionLabel ? getDirectionLabel(d) : (opTr[`dir${d.charAt(0).toUpperCase() + d.slice(1)}`]?.[langIndex] ?? d)
                return `${dirLabel} by outcome`
              })()}
            </Typography>
            <Box sx={{ ...opStyles.chartBox, minHeight: 320 }}>
              <Bar
                data={selectedDirectionOutcomeBarData}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { beginAtZero: true, ticks: { stepSize: 1 } },
                    y: { grid: { display: false } },
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={opStyles.chartPaper}>
            <Typography variant="subtitle1" sx={opStyles.chartTitle}>
              {opTr.outgoingCallsByOutcome?.[langIndex]}
            </Typography>
            <Box sx={{ ...opStyles.chartBox, minHeight: 320 }}>
              <Bar
                data={outgoingOutcomeBarData}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { beginAtZero: true, ticks: { stepSize: 1 } },
                    y: { grid: { display: false } },
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Paper sx={opStyles.chartPaperWide}>
        <Typography variant="subtitle1" sx={opStyles.chartTitle}>
          {opTr.ivrMusicHoldVoicemail?.[langIndex] ?? 'IVR / Music hold / Voicemail'}
        </Typography>
        <Box sx={{ ...opStyles.chartBox, minHeight: 140 }}>
          <Bar
            data={ivrVoicemailBarData}
            options={{
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { beginAtZero: true, ticks: { stepSize: 1 } },
                y: { grid: { display: false } },
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  )
}
